import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getFyersStatus, getFyersAuthUrl, completeFyersAuth, saveFyersToken, clearFyersToken, startEngine } from '../../api/client';

export default function FyersConnect() {
  const [status,    setStatus]    = useState(null);   // null=loading, true=ok, false=not connected
  const [feedInfo,  setFeedInfo]  = useState(null);   // {ws_connected, last_tick_age_seconds, ...}
  const [mode,      setMode]      = useState(null);   // 'oauth' | 'token'
  const [authUrl,   setAuthUrl]   = useState('');
  const [input,     setInput]     = useState('');
  const [saving,    setSaving]    = useState(false);
  const [msg,       setMsg]       = useState('');

  const checkStatus = useCallback(async () => {
    try {
      const res = await getFyersStatus();
      setStatus(res.data.authenticated);
      setFeedInfo({
        ws_connected: res.data.ws_connected,
        last_tick_age_seconds: res.data.last_tick_age_seconds,
        oldest_index_age_seconds: res.data.oldest_index_age_seconds,
        subscribed_count: res.data.subscribed_count,
        reconnect_attempts: res.data.reconnect_attempts,
      });
    } catch {
      setStatus(false);
      setFeedInfo(null);
    }
  }, []);

  useEffect(() => {
    checkStatus();
    // Re-check every 60s during market hours so the feed-health badge stays fresh.
    // (Previously every 5 min — too slow to surface WebSocket dropouts.)
    const t = setInterval(checkStatus, 60 * 1000);
    return () => clearInterval(t);
  }, [checkStatus]);

  // Audit T3 fix — auto-complete OAuth when the backend redirects back here
  // with the auth_code in URL fragment (#) or query (?). Uses a ref guard
  // instead of an empty dep array so we don't fight exhaustive-deps lint.
  const oauthConsumedRef = useRef(false);
  useEffect(() => {
    if (oauthConsumedRef.current) return;

    // Check fragment first (preferred), then query string as fallback.
    const hash    = window.location.hash || '';
    const search  = window.location.search || '';
    const haystack = hash + ' ' + search;
    const m = haystack.match(/(?:fyers_)?auth_code=([^&\s#]+)/);
    if (!m || !m[1]) return;

    // Mark consumed BEFORE the async work so a re-render triggered by setSaving
    // doesn't try to process the code again.
    oauthConsumedRef.current = true;

    const code = decodeURIComponent(m[1]);
    // Strip the auth_code from the URL bar so a refresh doesn't try to re-use
    // a now-consumed single-use code. Preserve everything except this param.
    const cleanedSearch = search.replace(/[?&](?:fyers_)?auth_code=[^&]*/g, '').replace(/^&/, '?');
    const cleanedHash   = hash.replace(/(?:fyers_)?auth_code=[^&]*/g, '').replace(/^#&/, '#').replace(/^#$/, '');
    window.history.replaceState(null, '', window.location.pathname + cleanedSearch + cleanedHash);

    // Submit via the existing authenticated POST endpoint
    (async () => {
      setSaving(true);
      setMsg('🔐 Auto-completing Fyers OAuth …');
      try {
        await completeFyersAuth(code);
        setMsg('✅ Fyers OAuth completed. Starting engine …');
        await startEngine().catch(() => {});
        await checkStatus();
        setMsg('✅ Fyers connected and engine started.');
        setTimeout(() => setMsg(''), 4000);
      } catch (e) {
        const detail = e.response?.data?.detail || e.message || 'Unknown error';
        setMsg('❌ OAuth auto-complete failed: ' + detail);
      } finally {
        setSaving(false);
      }
    })();
  }, [checkStatus]);

  const openOAuth = async () => {
    setMode('oauth');
    setMsg('');
    try {
      const res = await getFyersAuthUrl();
      const url = res.data.auth_url;
      setAuthUrl(url);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Unknown error';
      setMsg('Could not get auth URL: ' + detail);
    }
  };

  const submitOAuth = async () => {
    setSaving(true);
    setMsg('');
    try {
      // Accept any of:
      //   - just the raw auth_code           (e.g. eyJhb...vQ4)
      //   - URL with ?auth_code=…            (the Fyers redirect URL)
      //   - URL with #fyers_auth_code=…      (our backend's redirect to /)
      //   - URL with #auth_code=…            (defensive)
      let code = input.trim();
      // 1. Try matching auth_code OR fyers_auth_code in either query (?) or fragment (#).
      const m = code.match(/(?:fyers_)?auth_code=([^&\s]+)/);
      if (m && m[1]) {
        code = decodeURIComponent(m[1]);
      }
      await completeFyersAuth(code);
      setMsg('✅ Connected! Starting engine…');
      setMode(null);
      setInput('');
      await startEngine().catch(() => {});
      checkStatus();
      setMsg('✅ Fyers connected and engine started.');
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || 'Token exchange failed. Check the auth code.'));
    } finally {
      setSaving(false);
    }
  };

  const submitToken = async () => {
    setSaving(true);
    setMsg('');
    try {
      await saveFyersToken(input.trim());
      setMsg('✅ Token saved! Starting engine…');
      setMode(null);
      setInput('');
      await startEngine().catch(() => {});
      checkStatus();
      setMsg('✅ Fyers connected and engine started.');
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || 'Failed to save token.'));
    } finally {
      setSaving(false);
    }
  };

  const disconnectFyers = async () => {
    setSaving(true);
    setMsg('');
    try {
      await clearFyersToken();
      setMsg('Token cleared. Engine stopped.');
      checkStatus();
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || 'Failed to clear token.'));
    } finally {
      setSaving(false);
    }
  };

  if (status === null) return null;   // still loading

  // Feed health badge — distinct from token validity.
  // Three signals contribute, in priority order:
  //   1. ws_connected: false           → red "Feed disconnected"
  //   2. indices stale > 60s           → orange "Indices stale (Xs)"
  //   3. any tick recent (<60s)        → green "Feed live"
  //   4. global tick age > 60s         → orange "Feed stale (Xs)"
  const feedConnected = feedInfo?.ws_connected;
  const tickAge       = feedInfo?.last_tick_age_seconds;
  const idxAge        = feedInfo?.oldest_index_age_seconds;

  let feedColor, feedLabel, feedTitle;
  if (!feedConnected) {
    feedColor = 'var(--red)';
    feedLabel = 'Feed disconnected';
    feedTitle = `WebSocket disconnected. Watchdog will retry during market hours (${feedInfo?.reconnect_attempts || 0} attempts so far)`;
  } else if (idxAge != null && idxAge > 60) {
    // Specifically the case the user hit: feed "live" overall but index ticks stuck.
    feedColor = 'var(--orange)';
    feedLabel = `Indices stale (${Math.round(idxAge)}s)`;
    feedTitle = `Connected but NIFTY/SENSEX/BANKNIFTY ticks are ${Math.round(idxAge)}s old. The watchdog will force-reconnect if this exceeds 3 minutes.`;
  } else if (tickAge != null && tickAge > 60) {
    feedColor = 'var(--orange)';
    feedLabel = `Feed stale (${Math.round(tickAge)}s)`;
    feedTitle = `Connected but no ticks for ${Math.round(tickAge)}s.`;
  } else {
    feedColor = 'var(--green)';
    feedLabel = 'Feed live';
    feedTitle = `WebSocket connected, last tick ${tickAge != null ? Math.round(tickAge) + 's ago' : 'unknown'}, ${feedInfo?.subscribed_count || 0} symbols subscribed`;
  }

  if (status === true) return (
    <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 16px', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        {/* Token badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} title="Fyers access token in Redis, valid until midnight IST">
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--green)', boxShadow: '0 0 6px var(--green)' }} />
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>Token OK</span>
        </div>
        {/* Feed badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} title={feedTitle}>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: feedColor, boxShadow: `0 0 6px ${feedColor}` }} />
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>{feedLabel}</span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {msg && (
          <span style={{ fontSize: 12, color: msg.startsWith('❌') ? 'var(--red)' : 'var(--muted)' }}>
            {msg}
          </span>
        )}
        <button
          className="btn btn-ghost"
          style={{ fontSize: 12, color: 'var(--red)', borderColor: 'var(--red)' }}
          onClick={disconnectFyers}
          disabled={saving}
          title="Clear stored Fyers token and stop the engine"
        >
          {saving ? 'Clearing…' : '🔌 Disconnect'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="card" style={{ marginBottom: 16, borderLeft: '3px solid var(--accent)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>
            ⚠️ Fyers Not Connected
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
            Live market data and trading require a valid Fyers access token.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={openOAuth}>
            🔐 Connect via OAuth
          </button>
          <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => { setMode('token'); setMsg(''); }}>
            📋 Paste Token
          </button>
        </div>
      </div>

      {/* OAuth flow */}
      {mode === 'oauth' && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 8px' }}>
            1. A Fyers login page has opened in a new tab.<br />
            2. Login and authorise the app.<br />
            3. You'll be redirected to a URL — paste the <b style={{ color: 'var(--text)' }}>full redirect URL</b> or just the <b style={{ color: 'var(--text)' }}>auth_code</b> below.
          </p>
          {authUrl && (
            <div style={{ marginBottom: 8 }}>
              <a href={authUrl} target="_blank" rel="noopener noreferrer"
                 style={{ fontSize: 11, color: 'var(--accent)' }}>
                🔗 Open Fyers login again
              </a>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="input" placeholder="Paste redirect URL or auth_code here"
              style={{ flex: 1 }} value={input} onChange={e => setInput(e.target.value)} />
            <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={submitOAuth} disabled={saving || !input}>
              {saving ? 'Saving…' : 'Confirm'}
            </button>
          </div>
        </div>
      )}

      {/* Direct token paste */}
      {mode === 'token' && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 8px' }}>
            Run <code style={{ background: 'var(--bg)', padding: '1px 5px', borderRadius: 3 }}>python get_token.py</code> locally
            to generate a token, then paste it below.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="input" placeholder="Paste Fyers access token"
              style={{ flex: 1 }} value={input} onChange={e => setInput(e.target.value)} />
            <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={submitToken} disabled={saving || !input}>
              {saving ? 'Saving…' : 'Save Token'}
            </button>
          </div>
        </div>
      )}

      {msg && (
        <div style={{ marginTop: 10, fontSize: 12, color: msg.startsWith('✅') ? 'var(--green)' : 'var(--red)' }}>
          {msg}
        </div>
      )}
    </div>
  );
}
